/** Weergave-helpers die de opmaak van het huidige dashboard aanhouden. */

/*
 * Getalopmaak, gemeten aan het oude dashboard.
 *
 * Oud is hierin niet uniform, en dat is met opzet overgenomen:
 *
 * * bedragen lopen door één helper die `toLocaleString("nl-NL")` gebruikt —
 *   "€254,90", punt als duizendtal, komma als decimaal. In de hele bundel
 *   staat 102× "nl-NL" tegen 7× "en-GB";
 * * gehele getallen worden ONGEGROEPEERD getoond: oud schrijft 15845 en
 *   28413, niet 15.845;
 * * percentages krijgen altijd één decimaal: 0.0%, 25.0%.
 *
 * De Engelse bedragen die op Earnings te zien zijn (€397.49) komen niet uit
 * een formatter maar uit twee tabellen die daar zelf "€" + toFixed(2) van
 * maken; die staan in `pages/Earnings.tsx` en horen zo te blijven.
 */

/** Geheel getal zoals oud: zonder duizendtalscheiding. */
export function num(value: unknown): string {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n)) return '0'
  return String(Number.isInteger(n) ? n : Math.round(n * 100) / 100)
}

/** Bedrag zoals oud: "€254,90". */
export function eur(value: unknown): string {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n)) return '€0,00'
  // Bewust niet `style: 'currency'`: die zet er een vaste spatie tussen.
  // Oud plakt het euroteken direct tegen het bedrag.
  return '€' + n.toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * FanCentro-prijs in credits: "4,000 credits".
 *
 * 02-10-2026 (Jordy): FanCentro rekent in CREDITS (100 credits = €1), niet in
 * euro's. De Mass Message-historie zette er eur() omheen en maakte van een
 * campagne van 4000 credits "€4.000,00". Engelse groepering met komma, zoals
 * de bestaande hint "1,000 credits = €10" in het chatvenster. Accepteert ook
 * de tekst uit een prijsveld ("800", "4000").
 */
export function credits(value: unknown): string {
  const n = Number(String(value ?? 0).replace(',', '.'))
  const v = Number.isFinite(n) ? n : 0
  return `${v.toLocaleString('en-US', { maximumFractionDigits: 2 })} credits`
}

/**
 * Een prijs in de eenheid van het platform (02-10-2026, Jordy): F2F in
 * euro's ("€15,00"), FanCentro in credits ("4,000 credits"). Dezelfde regel
 * als overal in de chat: alles wat niet F2F is, is FanCentro.
 */
export function prijsVoorPlatform(value: unknown, platform: unknown): string {
  return String(platform ?? '').toLowerCase() === 'f2f' ? eur(value) : credits(value)
}

/** Percentage zoals oud: altijd één decimaal ("0.0%", "25.0%"). */
export function pct(value: unknown, digits = 1): string {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n)) return `${(0).toFixed(digits)}%`
  return `${n.toFixed(digits)}%`
}

export function dt(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ago(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return '—'
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000)
  // Engels, zoals oud: "23m ago", "4h ago", "3d ago".
  if (s < 60) return `${Math.round(s)}s ago`
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

/** Initiaal voor het ronde avatarblokje, zoals de stijlreferentie. */
export function initial(name: unknown): string {
  const s = String(name ?? '').replace(/[^A-Za-z]/g, '')
  return s ? s[0].toUpperCase() : '·'
}

/** "01-09, 12:35" — het paneelformaat van de oude pagina. */
export function dtShort(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}, ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** "1-9-2026, 06:05:11" — het formaat van de kolom Last activity. */
export function dtFull(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}, ` +
         `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/** Wachttijd als "3d 19u" / "4u 12m", precies zoals de oude panelen. */
export function waited(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return '—'
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000)
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const mins = Math.floor((s % 3600) / 60)
  if (days > 0) return `${days}d ${hours}u`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

/** "01-09, 15:22:53" — het tijdformaat van de Live Feed. */
export function dtSec(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}, ` +
         `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/** "05-09 14:40:46" — het formaat van de transactietabel in Earnings. */
export function dtTx(value: unknown): string {
  if (!value) return '—'
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${String(d.getFullYear()).slice(2)}, ` +
         `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/**
 * "2026-08-16" — alleen de dag uit een ISO-tijdstempel.
 *
 * Bewust de string afsnijden en niet via `new Date()`: die zet om naar de
 * tijdzone van de browser, en dan schuift een betaling van 00:30 UTC een dag
 * terug. Oud snijdt ook af, dus zo blijven de datums gelijk.
 */
export function dtDay(value: unknown): string {
  return value ? String(value).slice(0, 10) : ''
}
